// src/components/OrdersView.js
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Receipt,
  Calendar,
  Trash2,
  Search,
  X,
  Eye,
} from "lucide-react";

import { getOrders, deleteOrder } from "../api";
import { getRoleFromToken } from "../auth";

export default function OrdersView() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchOrderNumber, setSearchOrderNumber] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [selectedOrder, setSelectedOrder] = useState(null);

  const role = getRoleFromToken();
  const firstLoadRef = useRef(true);

  // ---------------------------------------------------------
  // LOAD ORDERS
  // ---------------------------------------------------------

  const loadOrders = useCallback(async (silent = false) => {
    try {
      if (!silent && firstLoadRef.current) {
        setLoading(true);
      }

      const data = await getOrders();

      const orderList = Array.isArray(data) ? data : [];

      setOrders((prev) => {
        return JSON.stringify(prev) !== JSON.stringify(orderList)
          ? orderList
          : prev;
      });

      firstLoadRef.current = false;
    } catch (e) {
      console.error("Error loading orders:", e);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  // ---------------------------------------------------------
  // INITIAL LOAD + AUTO REFRESH
  // ---------------------------------------------------------

  useEffect(() => {
    loadOrders(false);

    const intervalId = setInterval(() => {
      loadOrders(true);
    }, 5000);

    return () => clearInterval(intervalId);
  }, [loadOrders]);

  // ---------------------------------------------------------
  // FORMAT DATE
  // ---------------------------------------------------------

  const formatDate = (dateString) => {
    if (!dateString) return "";

    return new Date(dateString).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // ---------------------------------------------------------
  // FORMAT ORDER TYPE
  // ---------------------------------------------------------

  const getOrderType = (order) => {
    if (order?.orderType === "TAKEAWAY") {
      return {
        label: "Takeaway",
        icon: "🥡",
        className: "bg-green-100 text-green-700",
      };
    }

    return {
      label: "Dine In",
      icon: "🍽️",
      className: "bg-blue-100 text-blue-700",
    };
  };

  // ---------------------------------------------------------
  // ORDER CHANNEL / PAYMENT
  // ---------------------------------------------------------

  const getOrderChannel = (order) => {
    switch (String(order?.orderChannel || "POS").toUpperCase()) {
      case "SWIGGY":
        return "Swiggy";
      case "ZOMATO":
        return "Zomato";
      default:
        return "Direct POS";
    }
  };

  const getCashAmount = (order) =>
    Number(order?.cashAmount || 0);

  const getOnlineAmount = (order) =>
    Number(order?.onlineAmount || 0);

  const getPaidAmount = (order) =>
    getCashAmount(order) + getOnlineAmount(order);

  const getBalanceDue = (order) =>
    Math.max(0, getTotal(order) - getPaidAmount(order));

  const getPaymentStatus = (order) => {
    const status = String(order?.paymentStatus || "").toUpperCase();

    if (status === "PAID" || status === "PARTIALLY_PAID" || status === "UNPAID") {
      return status.replace("_", " ");
    }

    const paid = getPaidAmount(order);
    if (paid >= getTotal(order)) return "PAID";
    if (paid > 0) return "PARTIALLY PAID";
    return "UNPAID";
  };

  // ---------------------------------------------------------
  // STATUS
  // ---------------------------------------------------------

  const getStatusClass = (status) => {
    switch (status?.toUpperCase()) {
      case "READY":
        return "bg-green-100 text-green-700";

      case "PENDING":
        return "bg-yellow-100 text-yellow-700";

      case "COMPLETED":
        return "bg-green-100 text-green-700";

      case "DRAFT":
        return "bg-slate-100 text-slate-700";

      default:
        return "bg-slate-100 text-slate-700";
    }
  };

  // ---------------------------------------------------------
  // TOTAL ITEMS
  // ---------------------------------------------------------

  const getTotalItems = (order) => {
    return (order?.orderItems || []).reduce(
      (sum, item) => sum + Number(item.quantity || 0),
      0
    );
  };

  // ---------------------------------------------------------
  // DISCOUNT
  // ---------------------------------------------------------

  const getDiscountPercent = (order) => {
    return Number(order?.discountPercent || 0);
  };

  const getDiscountAmount = (order) => {
    return Number(order?.discountAmount || 0);
  };

  // ---------------------------------------------------------
  // SUBTOTAL
  // ---------------------------------------------------------

  const getSubtotal = (order) => {
    if (order?.subtotal !== undefined && order?.subtotal !== null) {
      return Number(order.subtotal || 0);
    }

    return (order?.orderItems || []).reduce(
      (sum, item) =>
        sum +
        Number(item.price || 0) *
          Number(item.quantity || 0),
      0
    );
  };

  // ---------------------------------------------------------
  // TAX
  // ---------------------------------------------------------

  const getTaxAmount = (order) => {
    if (order?.taxAmount !== undefined && order?.taxAmount !== null) {
      return Number(order.taxAmount || 0);
    }

    return (order?.orderItems || []).reduce(
      (sum, item) => sum + Number(item.taxAmount || 0),
      0
    );
  };

  // ---------------------------------------------------------
  // TOTAL
  // ---------------------------------------------------------

  const getTotal = (order) => {
    if (
      order?.totalAmount !== undefined &&
      order?.totalAmount !== null
    ) {
      return Number(order.totalAmount || 0);
    }

    return (
      getSubtotal(order) -
      getDiscountAmount(order) +
      getTaxAmount(order)
    );
  };

  // ---------------------------------------------------------
  // DELETE
  // ---------------------------------------------------------

  const handleDelete = async (id) => {
    if (role !== "ADMIN") {
      alert("Only admin can delete orders.");
      return;
    }

    if (!window.confirm("Delete this order?")) {
      return;
    }

    try {
      await deleteOrder(id);

      if (selectedOrder?.id === id) {
        setSelectedOrder(null);
      }

      await loadOrders(true);
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete order");
    }
  };

  // ---------------------------------------------------------
  // CLEAR FILTERS
  // ---------------------------------------------------------

  const clearFilters = () => {
    setSearchOrderNumber("");
    setFromDate("");
    setToDate("");
  };

  // ---------------------------------------------------------
  // FILTER
  // ---------------------------------------------------------

  const filteredOrders = orders.filter((order) => {
    const search = searchOrderNumber.trim().toLowerCase();

    const matchesOrderNumber =
      search === "" ||
      String(order.orderNumber || "")
        .toLowerCase()
        .includes(search);

    if (!matchesOrderNumber) {
      return false;
    }

    if (fromDate || toDate) {
      if (!order.createdAt) {
        return false;
      }

      const orderDate = new Date(order.createdAt);

      const orderDateString = `${orderDate.getFullYear()}-${String(
        orderDate.getMonth() + 1
      ).padStart(2, "0")}-${String(
        orderDate.getDate()
      ).padStart(2, "0")}`;

      if (fromDate && orderDateString < fromDate) {
        return false;
      }

      if (toDate && orderDateString > toDate) {
        return false;
      }
    }

    return true;
  });

  // ---------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------

  return (
    <div className="p-4 sm:p-8 h-full overflow-auto">

      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">

        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-800">
            Order History
          </h2>

          <p className="text-sm text-slate-500 mt-1">
            Search and manage your restaurant orders
          </p>
        </div>

        <div className="bg-white shadow-sm rounded-lg px-4 py-2">
          <div className="text-xs text-slate-500">
            Showing Orders
          </div>

          <div className="text-xl font-bold text-slate-800">
            {filteredOrders.length}
          </div>
        </div>
      </div>

      {/* SEARCH + FILTER */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">

          {/* SEARCH */}
          <div className="xl:col-span-2">

            <label className="block text-sm font-medium text-slate-600 mb-1">
              Search Order Number
            </label>

            <div className="relative">

              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />

              <input
                type="text"
                value={searchOrderNumber}
                onChange={(e) =>
                  setSearchOrderNumber(e.target.value)
                }
                placeholder="Search by order number..."
                className="w-full border border-slate-300 rounded-lg pl-10 pr-4 py-2.5 outline-none focus:ring-2 focus:ring-blue-500"
              />

            </div>
          </div>

          {/* FROM */}
          <div>

            <label className="block text-sm font-medium text-slate-600 mb-1">
              From Date
            </label>

            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2.5 outline-none focus:ring-2 focus:ring-blue-500"
            />

          </div>

          {/* TO */}
          <div>

            <label className="block text-sm font-medium text-slate-600 mb-1">
              To Date
            </label>

            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2.5 outline-none focus:ring-2 focus:ring-blue-500"
            />

          </div>

        </div>

        {(searchOrderNumber || fromDate || toDate) && (
          <div className="mt-4 flex justify-end">

            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium"
            >
              <X size={16} />
              Clear Filters
            </button>

          </div>
        )}

      </div>

      {/* LOADING */}
      {loading ? (

        <div className="text-center py-12">
          <p className="text-slate-500">
            Loading orders…
          </p>
        </div>

      ) : filteredOrders.length === 0 ? (

        <div className="text-center py-12 bg-white rounded-xl shadow-sm">

          <Receipt
            size={48}
            className="mx-auto mb-4 text-slate-300"
          />

          {orders.length === 0 ? (

            <p className="text-slate-500">
              No orders yet
            </p>

          ) : (

            <>
              <p className="text-slate-600 font-medium">
                No orders found
              </p>

              <p className="text-sm text-slate-400 mt-1">
                Try changing your search or date filters.
              </p>
            </>

          )}

        </div>

      ) : (

        /* ORDER LIST */
        <div className="space-y-4">

          {filteredOrders.map((order) => {

            const orderType = getOrderType(order);
            const orderChannel = getOrderChannel(order);
            const subtotal = getSubtotal(order);
            const taxAmount = getTaxAmount(order);
            const discountAmount = getDiscountAmount(order);
            const discountPercent = getDiscountPercent(order);
            const total = getTotal(order);

            return (

              <div
                key={order.id}
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6"
              >

                {/* HEADER */}
                <div className="flex flex-col sm:flex-row sm:justify-between gap-4 mb-4">

                  <div>

                    <div className="flex flex-wrap items-center gap-2">

                      <h3 className="text-lg font-bold text-slate-800">
                        {order.orderNumber}
                      </h3>

                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${orderType.className}`}
                      >
                        {orderType.icon}
                        {orderType.label}
                      </span>

                      <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-semibold">
                        {orderChannel}
                      </span>

                      <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold">
                        {getPaymentStatus(order)}
                      </span>

                      {order.orderType === "DINE_IN" &&
                        order.table && (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold">
                            Table: {order.table.tableName}
                          </span>
                        )}

                    </div>

                    <div className="flex items-center gap-2 text-xs text-slate-500 mt-2">

                      <Calendar size={14} />

                      {formatDate(order.createdAt)}

                    </div>

                  </div>

                  {/* TOTAL */}
                  <div className="sm:text-right">

                    <div className="text-xl font-bold text-green-600">
                      ₹{total.toFixed(2)}
                    </div>

                    <span
                      className={`inline-block mt-1 px-3 py-1 rounded-full text-xs font-medium ${getStatusClass(
                        order.status
                      )}`}
                    >
                      {order.status}
                    </span>

                  </div>

                </div>

                {/* ORDER NOTE */}
                {order.specialInstructions &&
                  order.specialInstructions.trim() !== "" && (

                    <div className="mb-4 rounded-lg border border-yellow-200 bg-yellow-50 p-3">

                      <div className="text-xs font-semibold text-yellow-800 mb-1">
                        📝 Order Note
                      </div>

                      <div className="text-sm text-slate-700">
                        {order.specialInstructions}
                      </div>

                    </div>
                  )}

                {/* ITEMS */}
                <div className="border-t pt-4">

                  <div className="flex justify-between items-center mb-2">

                    <h4 className="font-semibold text-slate-700">
                      Items
                    </h4>

                    <span className="text-xs text-slate-500">
                      {getTotalItems(order)} item
                      {getTotalItems(order) !== 1 ? "s" : ""}
                    </span>

                  </div>

                  <div className="space-y-2 text-sm">

                    {(order.orderItems || []).map((item) => {

                      const itemPrice = Number(item.price || 0);
                      const quantity = Number(item.quantity || 0);
                      const itemTax = Number(item.taxAmount || 0);
                      const itemLineTotal =
                        Number(item.lineTotal || 0);

                      return (

                        <div
                          key={item.id}
                          className="border-b last:border-b-0 pb-2"
                        >

                          <div className="flex justify-between gap-4">

                            <span className="text-slate-700">
                              {quantity} ×{" "}
                              {item.menuItem?.name || "Unknown Item"}
                            </span>

                            <span className="font-medium text-slate-700 whitespace-nowrap">
                              ₹
                              {(
                                itemLineTotal ||
                                itemPrice * quantity
                              ).toFixed(2)}
                            </span>

                          </div>

                          {/* ITEM TAX */}
                          {itemTax > 0 && (
                            <div className="flex justify-between text-xs text-slate-500 mt-1">

                              <span>
                                GST{" "}
                                {Number(
                                  item.taxRate || 0
                                ).toFixed(2)}
                                %
                              </span>

                              <span>
                                + ₹{itemTax.toFixed(2)}
                              </span>

                            </div>
                          )}

                          {item.specialInstructions &&
                            item.specialInstructions.trim() !== "" && (
                              <div className="text-xs text-yellow-700 mt-1">
                                📝 {item.specialInstructions}
                              </div>
                            )}

                        </div>

                      );
                    })}

                  </div>

                </div>

                {/* BILL SUMMARY */}
                <div className="mt-5 pt-4 border-t">

                  <div className="space-y-2 text-sm">

                    <div className="flex justify-between">
                      <span className="text-slate-500">
                        Subtotal
                      </span>

                      <span className="font-medium">
                        ₹{subtotal.toFixed(2)}
                      </span>
                    </div>

                    {discountAmount > 0 && (
                      <div className="flex justify-between text-red-600">

                        <span>
                          Discount
                          {discountPercent > 0
                            ? ` (${discountPercent.toFixed(2)}%)`
                            : ""}
                        </span>

                        <span>
                          - ₹{discountAmount.toFixed(2)}
                        </span>

                      </div>
                    )}

                    <div className="flex justify-between text-slate-600">

                      <span>
                        GST / Tax
                      </span>

                      <span>
                        ₹{taxAmount.toFixed(2)}
                      </span>

                    </div>

                    <div className="flex justify-between items-center pt-3 border-t">

                      <span className="text-lg font-semibold text-slate-700">
                        Total
                      </span>

                      <span className="text-2xl font-bold text-green-600">
                        ₹{total.toFixed(2)}
                      </span>

                    </div>

                  </div>

                </div>

                {/* ACTIONS */}
                <div className="mt-4 pt-4 border-t flex flex-col sm:flex-row sm:justify-end gap-2">

                  <button
                    onClick={() => setSelectedOrder(order)}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200"
                  >
                    <Eye size={16} />
                    View Details
                  </button>

                  {role === "ADMIN" && (
                    <button
                      onClick={() => handleDelete(order.id)}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200"
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  )}

                </div>

              </div>

            );
          })}

        </div>
      )}

      {/* =====================================================
          ORDER DETAILS MODAL
      ====================================================== */}

      {selectedOrder && (

        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setSelectedOrder(null)}
        >

          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >

            {/* MODAL HEADER */}
            <div className="sticky top-0 bg-white border-b p-5 flex justify-between items-start">

              <div>

                <h2 className="text-xl font-bold text-slate-800">
                  Order Details
                </h2>

                <p className="text-sm text-slate-500 mt-1">
                  {selectedOrder.orderNumber}
                </p>

              </div>

              <button
                onClick={() => setSelectedOrder(null)}
                className="p-2 rounded-lg hover:bg-slate-100"
              >
                <X size={20} />
              </button>

            </div>

            {/* MODAL CONTENT */}
            <div className="p-5 space-y-5">

              {/* BASIC INFORMATION */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-xs text-slate-500">
                    Order Number
                  </div>

                  <div className="font-semibold text-slate-800 mt-1">
                    {selectedOrder.orderNumber}
                  </div>
                </div>

                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-xs text-slate-500">
                    Date & Time
                  </div>

                  <div className="font-semibold text-slate-800 mt-1">
                    {formatDate(selectedOrder.createdAt)}
                  </div>
                </div>

                <div className="bg-slate-50 rounded-lg p-3">

                  <div className="text-xs text-slate-500">
                    Order Type
                  </div>

                  <div className="font-semibold text-slate-800 mt-1">
                    {getOrderType(selectedOrder).icon}{" "}
                    {getOrderType(selectedOrder).label}
                  </div>

                </div>

                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-xs text-slate-500">
                    Order Channel
                  </div>
                  <div className="font-semibold text-slate-800 mt-1">
                    {getOrderChannel(selectedOrder)}
                  </div>
                </div>

                <div className="bg-slate-50 rounded-lg p-3">

                  <div className="text-xs text-slate-500">
                    Table
                  </div>

                  <div className="font-semibold text-slate-800 mt-1">

                    {selectedOrder.orderType === "DINE_IN" &&
                    selectedOrder.table
                      ? selectedOrder.table.tableName
                      : "—"}

                  </div>

                </div>

                <div className="bg-slate-50 rounded-lg p-3">

                  <div className="text-xs text-slate-500">
                    Status
                  </div>

                  <div className="mt-1">

                    <span
                      className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getStatusClass(
                        selectedOrder.status
                      )}`}
                    >
                      {selectedOrder.status}
                    </span>

                  </div>

                </div>

                <div className="bg-slate-50 rounded-lg p-3">

                  <div className="text-xs text-slate-500">
                    Total Amount
                  </div>

                  <div className="font-bold text-green-600 text-lg mt-1">
                    ₹{getTotal(selectedOrder).toFixed(2)}
                  </div>

                </div>

                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-xs text-slate-500">
                    Payment Status
                  </div>
                  <div className="font-semibold text-slate-800 mt-1">
                    {getPaymentStatus(selectedOrder)}
                  </div>
                </div>

              </div>

              {/* DISCOUNT */}
              <div>

                <h3 className="font-semibold text-slate-800 mb-2">
                  Billing Summary
                </h3>

                <div className="rounded-lg border bg-slate-50 p-4 space-y-2">

                  <div className="flex justify-between text-sm">
                    <span>Subtotal</span>

                    <span>
                      ₹{getSubtotal(selectedOrder).toFixed(2)}
                    </span>
                  </div>

                  {getDiscountAmount(selectedOrder) > 0 && (
                    <div className="flex justify-between text-sm text-red-600">

                      <span>
                        Discount (
                        {getDiscountPercent(
                          selectedOrder
                        ).toFixed(2)}
                        %)
                      </span>

                      <span>
                        - ₹
                        {getDiscountAmount(
                          selectedOrder
                        ).toFixed(2)}
                      </span>

                    </div>
                  )}

                  <div className="flex justify-between text-sm">

                    <span>
                      GST / Tax
                    </span>

                    <span>
                      ₹
                      {getTaxAmount(
                        selectedOrder
                      ).toFixed(2)}
                    </span>

                  </div>

                  <div className="border-t pt-3 flex justify-between">

                    <span className="font-semibold">
                      Total
                    </span>

                    <span className="font-bold text-green-600">
                      ₹
                      {getTotal(
                        selectedOrder
                      ).toFixed(2)}
                    </span>

                  </div>

                  <div className="border-t pt-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Cash Received</span>
                      <span>₹{getCashAmount(selectedOrder).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Online Received</span>
                      <span>₹{getOnlineAmount(selectedOrder).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-semibold">
                      <span>Total Paid</span>
                      <span>₹{getPaidAmount(selectedOrder).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-bold">
                      <span>Balance Due</span>
                      <span className={getBalanceDue(selectedOrder) > 0 ? "text-red-600" : "text-green-600"}>
                        ₹{getBalanceDue(selectedOrder).toFixed(2)}
                      </span>
                    </div>
                  </div>

                </div>

              </div>

              {/* ORDER NOTE */}
              <div>

                <h3 className="font-semibold text-slate-800 mb-2">
                  📝 Order Note
                </h3>

                {selectedOrder.specialInstructions &&
                selectedOrder.specialInstructions.trim() !== "" ? (

                  <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-slate-700">
                    {selectedOrder.specialInstructions}
                  </div>

                ) : (

                  <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-400">
                    No notes added for this order.
                  </div>

                )}

              </div>

              {/* ITEMS */}
              <div>

                <div className="flex justify-between items-center mb-3">

                  <h3 className="font-semibold text-slate-800">
                    Items
                  </h3>

                  <span className="text-sm text-slate-500">
                    {getTotalItems(selectedOrder)} total
                  </span>

                </div>

                <div className="border rounded-lg overflow-hidden">

                  {(selectedOrder.orderItems || []).map(
                    (item, index) => {

                      const price = Number(
                        item.price || 0
                      );

                      const quantity = Number(
                        item.quantity || 0
                      );

                      const lineTotal =
                        Number(item.lineTotal || 0) ||
                        price * quantity;

                      const taxRate = Number(
                        item.taxRate || 0
                      );

                      const taxAmount = Number(
                        item.taxAmount || 0
                      );

                      return (

                        <div
                          key={item.id}
                          className={`p-3 ${
                            index !==
                            selectedOrder.orderItems.length - 1
                              ? "border-b"
                              : ""
                          }`}
                        >

                          <div className="flex justify-between gap-4">

                            <div>

                              <div className="font-medium text-slate-800">
                                {item.menuItem?.name ||
                                  "Unknown Item"}
                              </div>

                              <div className="text-xs text-slate-500 mt-1">
                                ₹{price.toFixed(2)} ×{" "}
                                {quantity}
                              </div>

                              {taxRate > 0 && (
                                <div className="text-xs text-slate-500 mt-1">
                                  GST {taxRate.toFixed(2)}%
                                  {" • "}
                                  ₹{taxAmount.toFixed(2)}
                                </div>
                              )}

                              {item.specialInstructions &&
                                item.specialInstructions.trim() !== "" && (
                                  <div className="text-xs text-yellow-700 mt-1">
                                    📝{" "}
                                    {item.specialInstructions}
                                  </div>
                                )}

                            </div>

                            <div className="font-semibold text-slate-800">
                              ₹{lineTotal.toFixed(2)}
                            </div>

                          </div>

                        </div>

                      );
                    }
                  )}

                </div>

              </div>

              {/* FINAL TOTAL */}
              <div className="border-t pt-4 flex justify-between items-center">

                <span className="text-lg font-semibold text-slate-700">
                  Total
                </span>

                <span className="text-2xl font-bold text-green-600">
                  ₹{getTotal(selectedOrder).toFixed(2)}
                </span>

              </div>

            </div>

            {/* FOOTER */}
            <div className="border-t p-4 flex justify-end">

              <button
                onClick={() => setSelectedOrder(null)}
                className="px-5 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-900"
              >
                Close
              </button>

            </div>

          </div>

        </div>

      )}

    </div>
  );
}


// // src/components/Order-History.js

// import React, { useCallback, useEffect, useMemo, useState } from "react";
// import {
//   Receipt,
//   Calendar,
//   Trash2,
//   Search,
//   X,
//   Eye,
//   RefreshCw,
//   AlertCircle,
// } from "lucide-react";

// import { getOrders, deleteOrder } from "../api";
// import { getRoleFromToken } from "../auth";

// export default function OrderHistory() {
//   const [orders, setOrders] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [refreshing, setRefreshing] = useState(false);
//   const [error, setError] = useState("");
//   const [searchOrderNumber, setSearchOrderNumber] = useState("");
//   const [fromDate, setFromDate] = useState("");
//   const [toDate, setToDate] = useState("");
//   const [selectedOrder, setSelectedOrder] = useState(null);
//   const [deletingId, setDeletingId] = useState(null);

//   // Your web auth utility should return the current user's role.
//   const role = String(getRoleFromToken() || "USER")
//     .toUpperCase()
//     .replace(/^ROLE_/, "");

//   const isAdmin = role === "ADMIN";

//   // --------------------------------------------------
//   // LOAD ORDERS
//   // --------------------------------------------------

//   const loadOrders = useCallback(async (isRefresh = false) => {
//     try {
//       if (isRefresh) {
//         setRefreshing(true);
//       } else {
//         setLoading(true);
//       }

//       setError("");

//       const response = await getOrders();
//       const list = Array.isArray(response) ? response : [];

//       setOrders(list);
//     } catch (err) {
//       console.error("Error loading order history:", err);

//       setError(
//         err.response?.data?.message ||
//           err.message ||
//           "Unable to load orders. Please try again."
//       );
//     } finally {
//       setLoading(false);
//       setRefreshing(false);
//     }
//   }, []);

//   useEffect(() => {
//     loadOrders();
//   }, [loadOrders]);

//   // --------------------------------------------------
//   // HELPERS
//   // --------------------------------------------------

//   const formatDate = (value) => {
//     if (!value) return "—";

//     const date = new Date(value);

//     if (Number.isNaN(date.getTime())) return "—";

//     return date.toLocaleString("en-IN", {
//       day: "2-digit",
//       month: "short",
//       year: "numeric",
//       hour: "2-digit",
//       minute: "2-digit",
//     });
//   };

//   const formatCurrency = (value) =>
//     `₹${Number(value || 0).toLocaleString("en-IN", {
//       minimumFractionDigits: 2,
//       maximumFractionDigits: 2,
//     })}`;

//   const getSubtotal = (order) => {
//     if (order?.subtotal != null) {
//       return Number(order.subtotal) || 0;
//     }

//     return (order?.orderItems || []).reduce(
//       (sum, item) =>
//         sum +
//         Number(item.price || 0) * Number(item.quantity || 0),
//       0
//     );
//   };

//   const getTaxAmount = (order) => {
//     if (order?.taxAmount != null) {
//       return Number(order.taxAmount) || 0;
//     }

//     return (order?.orderItems || []).reduce(
//       (sum, item) => sum + Number(item.taxAmount || 0),
//       0
//     );
//   };

//   const getDiscountAmount = (order) =>
//     Number(order?.discountAmount || 0);

//   const getDiscountPercent = (order) =>
//     Number(order?.discountPercent || 0);

//   const getTotal = (order) => {
//     if (order?.totalAmount != null) {
//       return Number(order.totalAmount) || 0;
//     }

//     return (
//       getSubtotal(order) -
//       getDiscountAmount(order) +
//       getTaxAmount(order)
//     );
//   };

//   const getTotalItems = (order) =>
//     (order?.orderItems || []).reduce(
//       (sum, item) => sum + Number(item.quantity || 0),
//       0
//     );

//   const getOrderType = (order) => {
//     if (order?.orderType === "TAKEAWAY") {
//       return {
//         label: "Takeaway",
//         icon: "🥡",
//         className: "so-tag-herb",
//       };
//     }

//     return {
//       label: "Dine In",
//       icon: "🍽️",
//       className: "so-tag-chili",
//     };
//   };

//   const getStatusClass = (status) => {
//     switch (String(status || "").toUpperCase()) {
//       case "READY":
//       case "COMPLETED":
//         return "so-tag-herb";

//       case "PENDING":
//         return "so-tag-mustard";

//       case "CANCELLED":
//         return "so-tag-chili-solid";

//       case "DRAFT":
//         return "so-tag-stone";

//       default:
//         return "so-tag-stone";
//     }
//   };

//   // --------------------------------------------------
//   // SEARCH AND DATE FILTERS
//   // --------------------------------------------------

//   const filteredOrders = useMemo(() => {
//     const search = searchOrderNumber.trim().toLowerCase();

//     return orders.filter((order) => {
//       const orderNumber = String(order.orderNumber || "").toLowerCase();

//       if (search && !orderNumber.includes(search)) {
//         return false;
//       }

//       if (fromDate || toDate) {
//         if (!order.createdAt) return false;

//         const date = new Date(order.createdAt);

//         if (Number.isNaN(date.getTime())) return false;

//         const orderDate = [
//           date.getFullYear(),
//           String(date.getMonth() + 1).padStart(2, "0"),
//           String(date.getDate()).padStart(2, "0"),
//         ].join("-");

//         if (fromDate && orderDate < fromDate) return false;
//         if (toDate && orderDate > toDate) return false;
//       }

//       return true;
//     });
//   }, [orders, searchOrderNumber, fromDate, toDate]);

//   const clearFilters = () => {
//     setSearchOrderNumber("");
//     setFromDate("");
//     setToDate("");
//   };

//   // --------------------------------------------------
//   // DELETE ORDER — ADMIN ONLY
//   // --------------------------------------------------

//   const handleDelete = async (order) => {
//     if (!isAdmin) {
//       window.alert("Only administrators can delete orders.");
//       return;
//     }

//     const confirmed = window.confirm(
//       `Are you sure you want to delete order ${
//         order.orderNumber || order.id
//       }? This action cannot be undone.`
//     );

//     if (!confirmed) return;

//     try {
//       setDeletingId(order.id);
//       setError("");

//       await deleteOrder(order.id);

//       setOrders((previous) =>
//         previous.filter((item) => item.id !== order.id)
//       );

//       setSelectedOrder((previous) =>
//         previous?.id === order.id ? null : previous
//       );
//     } catch (err) {
//       console.error("Delete order failed:", err);

//       window.alert(
//         err.response?.data?.message ||
//           "Unable to delete the order. Please try again."
//       );
//     } finally {
//       setDeletingId(null);
//     }
//   };

//   // --------------------------------------------------
//   // ORDER CARD
//   // --------------------------------------------------

//   const renderOrder = (order) => {
//     const orderType = getOrderType(order);

//     return (
//       <article
//         key={order.id ?? order.orderNumber}
//         className="so-order-card p-4 sm:p-6"
//       >
//         <div className="mb-4 flex flex-col justify-between gap-4 sm:flex-row">
//           <div className="min-w-0">
//             <div className="flex flex-wrap items-center gap-2">
//               <h3 className="so-order-number break-all">
//                 {order.orderNumber || `Order #${order.id}`}
//               </h3>

//               <span
//                 className={`so-tag ${orderType.className}`}
//               >
//                 {orderType.icon} {orderType.label}
//               </span>

//               {order.orderType === "DINE_IN" && order.table && (
//                 <span className="so-tag so-tag-mustard">
//                   Table: {order.table.tableName}
//                 </span>
//               )}
//             </div>

//             <p className="mt-2 flex items-center gap-2 text-xs so-text-stone">
//               <Calendar size={14} />
//               {formatDate(order.createdAt)}
//             </p>
//           </div>

//           <div className="sm:text-right">
//             <p className="text-xl font-bold so-text-herb">
//               {formatCurrency(getTotal(order))}
//             </p>

//             <span
//               className={`so-tag mt-1 inline-block ${getStatusClass(
//                 order.status
//               )}`}
//             >
//               {order.status || "UNKNOWN"}
//             </span>
//           </div>
//         </div>

//         {order.specialInstructions?.trim() && (
//           <div className="so-note-box mb-4 p-3">
//             <p className="mb-1 text-xs font-semibold" style={{ color: "#854f0b" }}>
//               📝 Order Note
//             </p>
//             <p className="whitespace-pre-wrap text-sm so-text-ink">
//               {order.specialInstructions}
//             </p>
//           </div>
//         )}

//         <div className="so-solid-divider pt-4">
//           <div className="mb-3 flex items-center justify-between">
//             <h4 className="font-semibold so-text-ink">Items</h4>
//             <span className="text-xs so-text-stone">
//               {getTotalItems(order)} item
//               {getTotalItems(order) !== 1 ? "s" : ""}
//             </span>
//           </div>

//           {(order.orderItems || []).length === 0 ? (
//             <p className="text-sm so-text-stone-light">No items found.</p>
//           ) : (
//             <div className="space-y-3">
//               {(order.orderItems || []).map((item, index) => {
//                 const quantity = Number(item.quantity || 0);
//                 const price = Number(item.price || 0);
//                 const lineTotal =
//                   Number(item.lineTotal || 0) || price * quantity;
//                 const tax = Number(item.taxAmount || 0);

//                 return (
//                   <div
//                     key={item.id ?? `${order.id}-item-${index}`}
//                     className="so-dashed-divider flex justify-between gap-4 pb-2 last:border-0"
//                   >
//                     <div className="min-w-0">
//                       <p className="break-words text-sm so-text-ink">
//                         {quantity} × {item.menuItem?.name || "Unknown Item"}
//                       </p>

//                       {tax > 0 && (
//                         <p className="mt-1 text-xs so-text-stone">
//                           GST {Number(item.taxRate || 0).toFixed(2)}% ·{" "}
//                           {formatCurrency(tax)}
//                         </p>
//                       )}

//                       {item.specialInstructions?.trim() && (
//                         <p className="mt-1 text-xs" style={{ color: "#854f0b" }}>
//                           📝 {item.specialInstructions}
//                         </p>
//                       )}
//                     </div>

//                     <span className="whitespace-nowrap text-sm font-medium so-text-ink">
//                       {formatCurrency(lineTotal)}
//                     </span>
//                   </div>
//                 );
//               })}
//             </div>
//           )}
//         </div>

//         <div className="mt-4 so-solid-divider pt-4">
//           <div className="space-y-2 text-sm">
//             <div className="flex justify-between gap-4 so-text-ink">
//               <span className="so-text-stone">Subtotal</span>
//               <span>{formatCurrency(getSubtotal(order))}</span>
//             </div>

//             {getDiscountAmount(order) > 0 && (
//               <div className="flex justify-between gap-4" style={{ color: "var(--chili-dark)" }}>
//                 <span>
//                   Discount
//                   {getDiscountPercent(order) > 0
//                     ? ` (${getDiscountPercent(order).toFixed(2)}%)`
//                     : ""}
//                 </span>
//                 <span>
//                   -{formatCurrency(getDiscountAmount(order))}
//                 </span>
//               </div>
//             )}

//             <div className="flex justify-between gap-4 so-text-stone">
//               <span>GST / Tax</span>
//               <span>{formatCurrency(getTaxAmount(order))}</span>
//             </div>

//             <div className="flex justify-between gap-4 so-solid-divider pt-3">
//               <span className="text-base font-semibold so-text-ink">
//                 Total
//               </span>
//               <span className="text-xl font-bold so-text-herb">
//                 {formatCurrency(getTotal(order))}
//               </span>
//             </div>
//           </div>
//         </div>

//         <div className="mt-4 flex flex-wrap justify-end gap-2 so-solid-divider pt-4">
//           <button
//             type="button"
//             onClick={() => setSelectedOrder(order)}
//             className="so-btn-view inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium"
//           >
//             <Eye size={16} />
//             View Details
//           </button>

//           {isAdmin && (
//             <button
//               type="button"
//               onClick={() => handleDelete(order)}
//               disabled={deletingId === order.id}
//               className="so-btn-delete inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
//             >
//               <Trash2 size={16} />
//               {deletingId === order.id ? "Deleting..." : "Delete"}
//             </button>
//           )}
//         </div>
//       </article>
//     );
//   };

//   // --------------------------------------------------
//   // MAIN RENDER
//   // --------------------------------------------------

//   return (
//     <div className="so-order-history h-full min-h-0 overflow-y-auto p-4 sm:p-8">

//       {/* Scoped SwiftOrder theme — same tokens as the rest of the POS. */}
//       <style>{`
//         @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

//         .so-order-history {
//           --ink: #1c1a17;
//           --paper: #fbf9f4;
//           --paper-dim: #f2ede1;
//           --chili: #e4572e;
//           --chili-dark: #c43f1a;
//           --herb: #3f6b4f;
//           --herb-dark: #2d4f39;
//           --mustard: #f2a93b;
//           --stone: #6b6459;
//           --stone-light: #a49c8d;
//           --line: #ddd4c2;
//           --font-display: 'Bebas Neue', 'Arial Narrow', sans-serif;
//           --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
//           --font-mono: 'JetBrains Mono', 'Courier New', monospace;

//           background: var(--paper-dim);
//           background-image: radial-gradient(circle at 1px 1px, var(--line) 1px, transparent 1.6px);
//           background-size: 24px 24px;
//           font-family: var(--font-body);
//           color: var(--ink);
//         }

//         .so-order-history * {
//           box-sizing: border-box;
//         }

//         .so-text-ink { color: var(--ink); }
//         .so-text-stone { color: var(--stone); }
//         .so-text-stone-light { color: var(--stone-light); }
//         .so-text-herb { color: var(--herb-dark); }

//         .so-page-title {
//           font-family: var(--font-display);
//           font-size: 2rem;
//           letter-spacing: 0.01em;
//           color: var(--ink);
//         }

//         .so-page-sub {
//           font-size: 0.9rem;
//           color: var(--stone);
//           margin-top: 4px;
//         }

//         .so-count-card {
//           background: #fffdf8;
//           border: 1px solid var(--line);
//           border-radius: 10px;
//           padding: 8px 16px;
//         }

//         .so-count-label {
//           font-size: 0.72rem;
//           color: var(--stone-light);
//           font-family: var(--font-mono);
//           letter-spacing: 0.04em;
//         }

//         .so-count-value {
//           font-family: var(--font-display);
//           font-size: 1.5rem;
//           color: var(--ink);
//         }

//         .so-refresh-btn {
//           background: #fffdf8;
//           border: 1px solid var(--line);
//           color: var(--ink);
//           transition: background 0.15s ease, border-color 0.15s ease;
//         }

//         .so-refresh-btn:hover:not(:disabled) {
//           background: var(--paper-dim);
//           border-color: var(--chili);
//         }

//         .so-filter-panel {
//           background: #fffdf8;
//           border: 1px solid var(--line);
//           border-radius: 12px;
//         }

//         .so-filter-label {
//           font-size: 0.82rem;
//           font-weight: 600;
//           color: var(--stone);
//           margin-bottom: 6px;
//           display: block;
//         }

//         .so-filter-input {
//           width: 100%;
//           border: 1px solid var(--line);
//           border-radius: 8px;
//           padding: 10px 12px;
//           background: var(--paper);
//           color: var(--ink);
//           outline: none;
//           transition: border-color 0.15s ease, box-shadow 0.15s ease;
//         }

//         .so-filter-input:focus {
//           border-color: var(--chili);
//           box-shadow: 0 0 0 3px rgba(228, 87, 46, 0.15);
//         }

//         .so-clear-filters-btn {
//           background: var(--paper-dim);
//           color: var(--ink);
//           transition: background 0.15s ease;
//         }

//         .so-clear-filters-btn:hover {
//           background: var(--line);
//         }

//         .so-error-box {
//           background: #fdece6;
//           border: 1px solid var(--chili);
//           color: var(--chili-dark);
//           border-radius: 10px;
//         }

//         .so-loading-box, .so-empty-box {
//           background: #fffdf8;
//           border: 1px solid var(--line);
//           border-radius: 12px;
//         }

//         .so-order-card {
//           background: #fffdf8;
//           border: 1px solid var(--line);
//           border-radius: 12px;
//           box-shadow: 0 10px 26px -22px rgba(28, 26, 23, 0.3);
//         }

//         .so-order-number {
//           font-family: var(--font-display);
//           font-size: 1.3rem;
//           letter-spacing: 0.01em;
//           color: var(--ink);
//         }

//         .so-tag {
//           border-radius: 999px;
//           padding: 4px 11px;
//           font-size: 0.72rem;
//           font-weight: 700;
//           font-family: var(--font-mono);
//           letter-spacing: 0.02em;
//           display: inline-block;
//         }

//         .so-tag-herb {
//           background: #eaf3ee;
//           color: var(--herb-dark);
//         }

//         .so-tag-chili {
//           background: #fdece6;
//           color: var(--chili-dark);
//         }

//         .so-tag-chili-solid {
//           background: var(--chili);
//           color: var(--paper);
//         }

//         .so-tag-mustard {
//           background: #fdf3e2;
//           color: #854f0b;
//         }

//         .so-tag-stone {
//           background: var(--paper-dim);
//           color: var(--stone);
//         }

//         .so-note-box {
//           background: #fdf3e2;
//           border: 1px solid var(--mustard);
//           border-radius: 8px;
//         }

//         .so-solid-divider {
//           border-top: 1px solid var(--line);
//         }

//         .so-dashed-divider {
//           border-bottom: 1px dashed var(--line);
//         }

//         .so-btn-view {
//           background: #e6f1fb;
//           color: #0c447c;
//           border-radius: 8px;
//           transition: background 0.15s ease;
//         }

//         .so-btn-view:hover {
//           background: #b5d4f4;
//         }

//         .so-btn-delete {
//           background: #fdece6;
//           color: var(--chili-dark);
//           border-radius: 8px;
//           transition: background 0.15s ease;
//         }

//         .so-btn-delete:hover:not(:disabled) {
//           background: #f09595;
//         }

//         .so-modal-overlay {
//           background: rgba(28, 26, 23, 0.55);
//         }

//         .so-modal-card {
//           background: #fffdf8;
//           border-radius: 16px;
//         }

//         .so-modal-header {
//           background: #fffdf8;
//           border-bottom: 1px solid var(--line);
//         }

//         .so-modal-title {
//           font-family: var(--font-display);
//           font-size: 1.5rem;
//           color: var(--ink);
//         }

//         .so-modal-close {
//           color: var(--stone);
//           transition: background 0.15s ease, color 0.15s ease;
//         }

//         .so-modal-close:hover {
//           background: var(--paper-dim);
//           color: var(--chili);
//         }

//         .so-detail-box {
//           background: var(--paper-dim);
//           border-radius: 8px;
//         }

//         .so-summary-box {
//           background: var(--paper-dim);
//           border: 1px solid var(--line);
//           border-radius: 10px;
//         }

//         .so-items-box {
//           border: 1px solid var(--line);
//           border-radius: 10px;
//         }

//         .so-items-box > div + div {
//           border-top: 1px solid var(--line);
//         }

//         .so-modal-footer {
//           border-top: 1px solid var(--line);
//         }

//         .so-close-btn {
//           background: var(--ink);
//           color: var(--paper);
//           border-radius: 8px;
//           transition: background 0.15s ease;
//         }

//         .so-close-btn:hover {
//           background: #302c27;
//         }
//       `}</style>

//       <div className="mx-auto max-w-7xl">
//         <header className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
//           <div>
//             <h1 className="so-page-title">
//               Order History
//             </h1>
//             <p className="so-page-sub">
//               Search and manage your restaurant orders
//             </p>
//           </div>

//           <div className="flex items-center gap-3">
//             <div className="so-count-card">
//               <p className="so-count-label">SHOWING ORDERS</p>
//               <p className="so-count-value">
//                 {filteredOrders.length}
//               </p>
//             </div>

//             <button
//               type="button"
//               onClick={() => loadOrders(true)}
//               disabled={loading || refreshing}
//               title="Refresh orders"
//               className="so-refresh-btn inline-flex h-11 items-center justify-center gap-2 rounded-lg px-3 text-sm font-medium disabled:opacity-50"
//             >
//               <RefreshCw
//                 size={16}
//                 className={refreshing ? "animate-spin" : ""}
//               />
//               <span className="hidden sm:inline">Refresh</span>
//             </button>
//           </div>
//         </header>

//         {/* SEARCH AND FILTERS */}
//         <section className="so-filter-panel mb-6 p-4">
//           <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
//             <div className="xl:col-span-2">
//               <label
//                 htmlFor="order-search"
//                 className="so-filter-label"
//               >
//                 Search Order Number
//               </label>

//               <div className="relative">
//                 <Search
//                   size={18}
//                   className="absolute left-3 top-1/2 -translate-y-1/2"
//                   style={{ color: "var(--stone-light)" }}
//                 />

//                 <input
//                   id="order-search"
//                   type="search"
//                   value={searchOrderNumber}
//                   onChange={(event) =>
//                     setSearchOrderNumber(event.target.value)
//                   }
//                   placeholder="Search by order number..."
//                   className="so-filter-input"
//                   style={{ paddingLeft: "40px" }}
//                 />
//               </div>
//             </div>

//             <div>
//               <label
//                 htmlFor="order-from-date"
//                 className="so-filter-label"
//               >
//                 From Date
//               </label>

//               <input
//                 id="order-from-date"
//                 type="date"
//                 value={fromDate}
//                 max={toDate || undefined}
//                 onChange={(event) => setFromDate(event.target.value)}
//                 className="so-filter-input"
//               />
//             </div>

//             <div>
//               <label
//                 htmlFor="order-to-date"
//                 className="so-filter-label"
//               >
//                 To Date
//               </label>

//               <input
//                 id="order-to-date"
//                 type="date"
//                 value={toDate}
//                 min={fromDate || undefined}
//                 onChange={(event) => setToDate(event.target.value)}
//                 className="so-filter-input"
//               />
//             </div>
//           </div>

//           {(searchOrderNumber || fromDate || toDate) && (
//             <div className="mt-4 flex justify-end">
//               <button
//                 type="button"
//                 onClick={clearFilters}
//                 className="so-clear-filters-btn inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium"
//               >
//                 <X size={16} />
//                 Clear Filters
//               </button>
//             </div>
//           )}
//         </section>

//         {/* ERROR */}
//         {error && (
//           <div className="so-error-box mb-5 flex items-start gap-3 p-4 text-sm">
//             <AlertCircle size={20} className="mt-0.5 shrink-0" />
//             <div className="flex-1">
//               <p className="font-medium">Unable to load order history</p>
//               <p className="mt-1">{error}</p>
//             </div>
//             <button
//               type="button"
//               onClick={() => loadOrders()}
//               className="font-semibold underline"
//             >
//               Retry
//             </button>
//           </div>
//         )}

//         {/* LOADING */}
//         {loading ? (
//           <div className="so-loading-box py-16 text-center">
//             <RefreshCw
//               size={30}
//               className="mx-auto animate-spin"
//               style={{ color: "var(--chili)" }}
//             />
//             <p className="mt-4 text-sm so-text-stone">
//               Loading order history...
//             </p>
//           </div>
//         ) : filteredOrders.length === 0 ? (
//           <div className="so-empty-box px-4 py-16 text-center">
//             <Receipt size={48} className="mx-auto" style={{ color: "var(--line)" }} />

//             <h3 className="mt-4 font-semibold so-text-ink">
//               {error
//                 ? "Orders could not be loaded"
//                 : orders.length === 0
//                   ? "No orders yet"
//                   : "No orders found"}
//             </h3>

//             <p className="mt-2 text-sm so-text-stone">
//               {error
//                 ? "Check your connection and try again."
//                 : orders.length === 0
//                   ? "Orders will appear here when they become available."
//                   : "Try changing your search or date filters."}
//             </p>

//             {error && (
//               <button
//                 type="button"
//                 onClick={() => loadOrders()}
//                 className="so-close-btn mt-4 px-4 py-2 text-sm font-medium"
//               >
//                 Try Again
//               </button>
//             )}
//           </div>
//         ) : (
//           <div className="space-y-4">
//             {filteredOrders.map(renderOrder)}
//           </div>
//         )}
//       </div>

//       {/* ORDER DETAILS MODAL */}
//       {selectedOrder && (
//         <div
//           className="so-modal-overlay fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6"
//           onMouseDown={(event) => {
//             if (event.target === event.currentTarget) {
//               setSelectedOrder(null);
//             }
//           }}
//           onKeyDown={(event) => {
//             if (event.key === "Escape") setSelectedOrder(null);
//           }}
//         >
//           <div
//             role="dialog"
//             aria-modal="true"
//             aria-labelledby="order-details-title"
//             className="so-modal-card max-h-[90vh] w-full max-w-3xl overflow-y-auto shadow-xl"
//           >
//             <div className="so-modal-header sticky top-0 z-10 flex items-center justify-between p-5">
//               <div>
//                 <h2
//                   id="order-details-title"
//                   className="so-modal-title"
//                 >
//                   Order Details
//                 </h2>
//                 <p className="mt-1 text-sm so-text-stone">
//                   {selectedOrder.orderNumber || `Order #${selectedOrder.id}`}
//                 </p>
//               </div>

//               <button
//                 type="button"
//                 onClick={() => setSelectedOrder(null)}
//                 aria-label="Close order details"
//                 className="so-modal-close rounded-lg p-2"
//               >
//                 <X size={22} />
//               </button>
//             </div>

//             <div className="space-y-6 p-5">
//               <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
//                 <Detail
//                   label="Order Number"
//                   value={
//                     selectedOrder.orderNumber || `#${selectedOrder.id}`
//                   }
//                 />
//                 <Detail
//                   label="Date & Time"
//                   value={formatDate(selectedOrder.createdAt)}
//                 />
//                 <Detail
//                   label="Order Type"
//                   value={getOrderType(selectedOrder).label}
//                 />
//                 <Detail
//                   label="Table"
//                   value={
//                     selectedOrder.orderType === "DINE_IN" &&
//                     selectedOrder.table
//                       ? selectedOrder.table.tableName
//                       : "—"
//                   }
//                 />
//                 <Detail
//                   label="Status"
//                   value={selectedOrder.status || "UNKNOWN"}
//                 />
//                 <Detail
//                   label="Total Amount"
//                   value={formatCurrency(getTotal(selectedOrder))}
//                   emphasis
//                 />
//               </div>

//               <section>
//                 <h3 className="mb-3 font-semibold so-text-ink">
//                   Billing Summary
//                 </h3>

//                 <div className="so-summary-box space-y-3 p-4 text-sm">
//                   <SummaryRow
//                     label="Subtotal"
//                     value={formatCurrency(getSubtotal(selectedOrder))}
//                   />

//                   {getDiscountAmount(selectedOrder) > 0 && (
//                     <SummaryRow
//                       label={`Discount${
//                         getDiscountPercent(selectedOrder) > 0
//                           ? ` (${getDiscountPercent(selectedOrder).toFixed(2)}%)`
//                           : ""
//                       }`}
//                       value={`-${formatCurrency(
//                         getDiscountAmount(selectedOrder)
//                       )}`}
//                       negative
//                     />
//                   )}

//                   <SummaryRow
//                     label="GST / Tax"
//                     value={formatCurrency(getTaxAmount(selectedOrder))}
//                   />

//                   <div className="flex justify-between gap-4 so-solid-divider pt-3 text-base font-bold so-text-ink">
//                     <span>Total</span>
//                     <span className="so-text-herb">
//                       {formatCurrency(getTotal(selectedOrder))}
//                     </span>
//                   </div>
//                 </div>
//               </section>

//               <section>
//                 <h3 className="mb-3 font-semibold so-text-ink">
//                   Order Note
//                 </h3>
//                 <div className="so-summary-box p-4 text-sm so-text-ink">
//                   {selectedOrder.specialInstructions?.trim() ||
//                     "No notes added for this order."}
//                 </div>
//               </section>

//               <section>
//                 <div className="mb-3 flex items-center justify-between">
//                   <h3 className="font-semibold so-text-ink">
//                     Items
//                   </h3>
//                   <span className="text-sm so-text-stone">
//                     {getTotalItems(selectedOrder)} total
//                   </span>
//                 </div>

//                 {(selectedOrder.orderItems || []).length === 0 ? (
//                   <p className="so-items-box p-4 text-sm so-text-stone">
//                     No items found for this order.
//                   </p>
//                 ) : (
//                   <div className="so-items-box">
//                     {(selectedOrder.orderItems || []).map((item, index) => {
//                       const price = Number(item.price || 0);
//                       const quantity = Number(item.quantity || 0);
//                       const tax = Number(item.taxAmount || 0);
//                       const lineTotal =
//                         Number(item.lineTotal || 0) || price * quantity;

//                       return (
//                         <div
//                           key={item.id ?? `detail-item-${index}`}
//                           className="flex justify-between gap-4 p-4"
//                         >
//                           <div className="min-w-0">
//                             <p className="break-words font-medium so-text-ink">
//                               {item.menuItem?.name || "Unknown Item"}
//                             </p>
//                             <p className="mt-1 text-xs so-text-stone">
//                               {formatCurrency(price)} × {quantity}
//                             </p>

//                             {tax > 0 && (
//                               <p className="mt-1 text-xs so-text-stone">
//                                 GST {Number(item.taxRate || 0).toFixed(2)}% ·{" "}
//                                 {formatCurrency(tax)}
//                               </p>
//                             )}

//                             {item.specialInstructions?.trim() && (
//                               <p className="mt-1 text-xs" style={{ color: "#854f0b" }}>
//                                 📝 {item.specialInstructions}
//                               </p>
//                             )}
//                           </div>

//                           <p className="whitespace-nowrap font-semibold so-text-ink">
//                             {formatCurrency(lineTotal)}
//                           </p>
//                         </div>
//                       );
//                     })}
//                   </div>
//                 )}
//               </section>
//             </div>

//             <div className="so-modal-footer flex justify-end p-4">
//               <button
//                 type="button"
//                 onClick={() => setSelectedOrder(null)}
//                 className="so-close-btn px-5 py-2.5 text-sm font-medium"
//               >
//                 Close
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }

// function Detail({ label, value, emphasis = false }) {
//   return (
//     <div className="so-detail-box p-3">
//       <p className="text-xs" style={{ color: "#a49c8d" }}>{label}</p>
//       <p
//         className={`mt-1 break-words ${
//           emphasis
//             ? "text-lg font-bold so-text-herb"
//             : "font-semibold so-text-ink"
//         }`}
//       >
//         {value}
//       </p>
//     </div>
//   );
// }

// function SummaryRow({ label, value, negative = false }) {
//   return (
//     <div
//       className="flex justify-between gap-4"
//       style={{ color: negative ? "var(--chili-dark)" : "var(--stone)" }}
//     >
//       <span>{label}</span>
//       <span className="whitespace-nowrap font-medium">{value}</span>
//     </div>
//   );
// }







// // // src/components/Order-History.js

// // import React, { useCallback, useEffect, useMemo, useState } from "react";
// // import {
// //   Receipt,
// //   Calendar,
// //   Trash2,
// //   Search,
// //   X,
// //   Eye,
// //   RefreshCw,
// //   AlertCircle,
// // } from "lucide-react";

// // import { getOrders, deleteOrder } from "../api";
// // import { getRoleFromToken } from "../auth";

// // export default function OrderHistory() {
// //   const [orders, setOrders] = useState([]);
// //   const [loading, setLoading] = useState(true);
// //   const [refreshing, setRefreshing] = useState(false);
// //   const [error, setError] = useState("");
// //   const [searchOrderNumber, setSearchOrderNumber] = useState("");
// //   const [fromDate, setFromDate] = useState("");
// //   const [toDate, setToDate] = useState("");
// //   const [selectedOrder, setSelectedOrder] = useState(null);
// //   const [deletingId, setDeletingId] = useState(null);

// //   // Your web auth utility should return the current user's role.
// //   const role = String(getRoleFromToken() || "USER")
// //     .toUpperCase()
// //     .replace(/^ROLE_/, "");

// //   const isAdmin = role === "ADMIN";

// //   // --------------------------------------------------
// //   // LOAD ORDERS
// //   // --------------------------------------------------

// //   const loadOrders = useCallback(async (isRefresh = false) => {
// //     try {
// //       if (isRefresh) {
// //         setRefreshing(true);
// //       } else {
// //         setLoading(true);
// //       }

// //       setError("");

// //       const response = await getOrders();
// //       const list = Array.isArray(response) ? response : [];

// //       setOrders(list);
// //     } catch (err) {
// //       console.error("Error loading order history:", err);

// //       setError(
// //         err.response?.data?.message ||
// //           err.message ||
// //           "Unable to load orders. Please try again."
// //       );
// //     } finally {
// //       setLoading(false);
// //       setRefreshing(false);
// //     }
// //   }, []);

// //   useEffect(() => {
// //     loadOrders();
// //   }, [loadOrders]);

// //   // --------------------------------------------------
// //   // HELPERS
// //   // --------------------------------------------------

// //   const formatDate = (value) => {
// //     if (!value) return "—";

// //     const date = new Date(value);

// //     if (Number.isNaN(date.getTime())) return "—";

// //     return date.toLocaleString("en-IN", {
// //       day: "2-digit",
// //       month: "short",
// //       year: "numeric",
// //       hour: "2-digit",
// //       minute: "2-digit",
// //     });
// //   };

// //   const formatCurrency = (value) =>
// //     `₹${Number(value || 0).toLocaleString("en-IN", {
// //       minimumFractionDigits: 2,
// //       maximumFractionDigits: 2,
// //     })}`;

// //   const getSubtotal = (order) => {
// //     if (order?.subtotal != null) {
// //       return Number(order.subtotal) || 0;
// //     }

// //     return (order?.orderItems || []).reduce(
// //       (sum, item) =>
// //         sum +
// //         Number(item.price || 0) * Number(item.quantity || 0),
// //       0
// //     );
// //   };

// //   const getTaxAmount = (order) => {
// //     if (order?.taxAmount != null) {
// //       return Number(order.taxAmount) || 0;
// //     }

// //     return (order?.orderItems || []).reduce(
// //       (sum, item) => sum + Number(item.taxAmount || 0),
// //       0
// //     );
// //   };

// //   const getDiscountAmount = (order) =>
// //     Number(order?.discountAmount || 0);

// //   const getDiscountPercent = (order) =>
// //     Number(order?.discountPercent || 0);

// //   const getTotal = (order) => {
// //     if (order?.totalAmount != null) {
// //       return Number(order.totalAmount) || 0;
// //     }

// //     return (
// //       getSubtotal(order) -
// //       getDiscountAmount(order) +
// //       getTaxAmount(order)
// //     );
// //   };

// //   const getTotalItems = (order) =>
// //     (order?.orderItems || []).reduce(
// //       (sum, item) => sum + Number(item.quantity || 0),
// //       0
// //     );

// //   const getOrderType = (order) => {
// //     if (order?.orderType === "TAKEAWAY") {
// //       return {
// //         label: "Takeaway",
// //         icon: "🥡",
// //         className: "bg-green-100 text-green-700",
// //       };
// //     }

// //     return {
// //       label: "Dine In",
// //       icon: "🍽️",
// //       className: "bg-blue-100 text-blue-700",
// //     };
// //   };

// //   const getStatusClass = (status) => {
// //     switch (String(status || "").toUpperCase()) {
// //       case "READY":
// //       case "COMPLETED":
// //         return "bg-green-100 text-green-700";

// //       case "PENDING":
// //         return "bg-yellow-100 text-yellow-700";

// //       case "CANCELLED":
// //         return "bg-red-100 text-red-700";

// //       case "DRAFT":
// //         return "bg-slate-100 text-slate-700";

// //       default:
// //         return "bg-slate-100 text-slate-700";
// //     }
// //   };

// //   // --------------------------------------------------
// //   // SEARCH AND DATE FILTERS
// //   // --------------------------------------------------

// //   const filteredOrders = useMemo(() => {
// //     const search = searchOrderNumber.trim().toLowerCase();

// //     return orders.filter((order) => {
// //       const orderNumber = String(order.orderNumber || "").toLowerCase();

// //       if (search && !orderNumber.includes(search)) {
// //         return false;
// //       }

// //       if (fromDate || toDate) {
// //         if (!order.createdAt) return false;

// //         const date = new Date(order.createdAt);

// //         if (Number.isNaN(date.getTime())) return false;

// //         const orderDate = [
// //           date.getFullYear(),
// //           String(date.getMonth() + 1).padStart(2, "0"),
// //           String(date.getDate()).padStart(2, "0"),
// //         ].join("-");

// //         if (fromDate && orderDate < fromDate) return false;
// //         if (toDate && orderDate > toDate) return false;
// //       }

// //       return true;
// //     });
// //   }, [orders, searchOrderNumber, fromDate, toDate]);

// //   const clearFilters = () => {
// //     setSearchOrderNumber("");
// //     setFromDate("");
// //     setToDate("");
// //   };

// //   // --------------------------------------------------
// //   // DELETE ORDER — ADMIN ONLY
// //   // --------------------------------------------------

// //   const handleDelete = async (order) => {
// //     if (!isAdmin) {
// //       window.alert("Only administrators can delete orders.");
// //       return;
// //     }

// //     const confirmed = window.confirm(
// //       `Are you sure you want to delete order ${
// //         order.orderNumber || order.id
// //       }? This action cannot be undone.`
// //     );

// //     if (!confirmed) return;

// //     try {
// //       setDeletingId(order.id);
// //       setError("");

// //       await deleteOrder(order.id);

// //       setOrders((previous) =>
// //         previous.filter((item) => item.id !== order.id)
// //       );

// //       setSelectedOrder((previous) =>
// //         previous?.id === order.id ? null : previous
// //       );
// //     } catch (err) {
// //       console.error("Delete order failed:", err);

// //       window.alert(
// //         err.response?.data?.message ||
// //           "Unable to delete the order. Please try again."
// //       );
// //     } finally {
// //       setDeletingId(null);
// //     }
// //   };

// //   // --------------------------------------------------
// //   // ORDER CARD
// //   // --------------------------------------------------

// //   const renderOrder = (order) => {
// //     const orderType = getOrderType(order);

// //     return (
// //       <article
// //         key={order.id ?? order.orderNumber}
// //         className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6"
// //       >
// //         <div className="mb-4 flex flex-col justify-between gap-4 sm:flex-row">
// //           <div className="min-w-0">
// //             <div className="flex flex-wrap items-center gap-2">
// //               <h3 className="break-all text-lg font-bold text-slate-800">
// //                 {order.orderNumber || `Order #${order.id}`}
// //               </h3>

// //               <span
// //                 className={`rounded-full px-2.5 py-1 text-xs font-semibold ${orderType.className}`}
// //               >
// //                 {orderType.icon} {orderType.label}
// //               </span>

// //               {order.orderType === "DINE_IN" && order.table && (
// //                 <span className="rounded-full bg-purple-100 px-2.5 py-1 text-xs font-semibold text-purple-700">
// //                   Table: {order.table.tableName}
// //                 </span>
// //               )}
// //             </div>

// //             <p className="mt-2 flex items-center gap-2 text-xs text-slate-500">
// //               <Calendar size={14} />
// //               {formatDate(order.createdAt)}
// //             </p>
// //           </div>

// //           <div className="sm:text-right">
// //             <p className="text-xl font-bold text-green-600">
// //               {formatCurrency(getTotal(order))}
// //             </p>

// //             <span
// //               className={`mt-1 inline-block rounded-full px-3 py-1 text-xs font-medium ${getStatusClass(
// //                 order.status
// //               )}`}
// //             >
// //               {order.status || "UNKNOWN"}
// //             </span>
// //           </div>
// //         </div>

// //         {order.specialInstructions?.trim() && (
// //           <div className="mb-4 rounded-lg border border-yellow-200 bg-yellow-50 p-3">
// //             <p className="mb-1 text-xs font-semibold text-yellow-800">
// //               📝 Order Note
// //             </p>
// //             <p className="whitespace-pre-wrap text-sm text-slate-700">
// //               {order.specialInstructions}
// //             </p>
// //           </div>
// //         )}

// //         <div className="border-t pt-4">
// //           <div className="mb-3 flex items-center justify-between">
// //             <h4 className="font-semibold text-slate-700">Items</h4>
// //             <span className="text-xs text-slate-500">
// //               {getTotalItems(order)} item
// //               {getTotalItems(order) !== 1 ? "s" : ""}
// //             </span>
// //           </div>

// //           {(order.orderItems || []).length === 0 ? (
// //             <p className="text-sm text-slate-400">No items found.</p>
// //           ) : (
// //             <div className="space-y-3">
// //               {(order.orderItems || []).map((item, index) => {
// //                 const quantity = Number(item.quantity || 0);
// //                 const price = Number(item.price || 0);
// //                 const lineTotal =
// //                   Number(item.lineTotal || 0) || price * quantity;
// //                 const tax = Number(item.taxAmount || 0);

// //                 return (
// //                   <div
// //                     key={item.id ?? `${order.id}-item-${index}`}
// //                     className="flex justify-between gap-4 border-b border-slate-100 pb-2 last:border-0"
// //                   >
// //                     <div className="min-w-0">
// //                       <p className="break-words text-sm text-slate-700">
// //                         {quantity} × {item.menuItem?.name || "Unknown Item"}
// //                       </p>

// //                       {tax > 0 && (
// //                         <p className="mt-1 text-xs text-slate-500">
// //                           GST {Number(item.taxRate || 0).toFixed(2)}% ·{" "}
// //                           {formatCurrency(tax)}
// //                         </p>
// //                       )}

// //                       {item.specialInstructions?.trim() && (
// //                         <p className="mt-1 text-xs text-yellow-700">
// //                           📝 {item.specialInstructions}
// //                         </p>
// //                       )}
// //                     </div>

// //                     <span className="whitespace-nowrap text-sm font-medium text-slate-700">
// //                       {formatCurrency(lineTotal)}
// //                     </span>
// //                   </div>
// //                 );
// //               })}
// //             </div>
// //           )}
// //         </div>

// //         <div className="mt-4 border-t pt-4">
// //           <div className="space-y-2 text-sm">
// //             <div className="flex justify-between gap-4">
// //               <span className="text-slate-500">Subtotal</span>
// //               <span>{formatCurrency(getSubtotal(order))}</span>
// //             </div>

// //             {getDiscountAmount(order) > 0 && (
// //               <div className="flex justify-between gap-4 text-red-600">
// //                 <span>
// //                   Discount
// //                   {getDiscountPercent(order) > 0
// //                     ? ` (${getDiscountPercent(order).toFixed(2)}%)`
// //                     : ""}
// //                 </span>
// //                 <span>
// //                   -{formatCurrency(getDiscountAmount(order))}
// //                 </span>
// //               </div>
// //             )}

// //             <div className="flex justify-between gap-4 text-slate-600">
// //               <span>GST / Tax</span>
// //               <span>{formatCurrency(getTaxAmount(order))}</span>
// //             </div>

// //             <div className="flex justify-between gap-4 border-t pt-3">
// //               <span className="text-base font-semibold text-slate-700">
// //                 Total
// //               </span>
// //               <span className="text-xl font-bold text-green-600">
// //                 {formatCurrency(getTotal(order))}
// //               </span>
// //             </div>
// //           </div>
// //         </div>

// //         <div className="mt-4 flex flex-wrap justify-end gap-2 border-t pt-4">
// //           <button
// //             type="button"
// //             onClick={() => setSelectedOrder(order)}
// //             className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-100 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-200"
// //           >
// //             <Eye size={16} />
// //             View Details
// //           </button>

// //           {isAdmin && (
// //             <button
// //               type="button"
// //               onClick={() => handleDelete(order)}
// //               disabled={deletingId === order.id}
// //               className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-100 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-200 disabled:cursor-not-allowed disabled:opacity-50"
// //             >
// //               <Trash2 size={16} />
// //               {deletingId === order.id ? "Deleting..." : "Delete"}
// //             </button>
// //           )}
// //         </div>
// //       </article>
// //     );
// //   };

// //   // --------------------------------------------------
// //   // MAIN RENDER
// //   // --------------------------------------------------

// //   return (
// //     <div className="h-full min-h-0 overflow-y-auto bg-slate-50 p-4 sm:p-8">
// //       <div className="mx-auto max-w-7xl">
// //         <header className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
// //           <div>
// //             <h1 className="text-2xl font-bold text-slate-800 sm:text-3xl">
// //               Order History
// //             </h1>
// //             <p className="mt-1 text-sm text-slate-500">
// //               Search and manage your restaurant orders
// //             </p>
// //           </div>

// //           <div className="flex items-center gap-3">
// //             <div className="rounded-lg bg-white px-4 py-2 shadow-sm">
// //               <p className="text-xs text-slate-500">Showing Orders</p>
// //               <p className="text-xl font-bold text-slate-800">
// //                 {filteredOrders.length}
// //               </p>
// //             </div>

// //             <button
// //               type="button"
// //               onClick={() => loadOrders(true)}
// //               disabled={loading || refreshing}
// //               title="Refresh orders"
// //               className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
// //             >
// //               <RefreshCw
// //                 size={16}
// //                 className={refreshing ? "animate-spin" : ""}
// //               />
// //               <span className="hidden sm:inline">Refresh</span>
// //             </button>
// //           </div>
// //         </header>

// //         {/* SEARCH AND FILTERS */}
// //         <section className="mb-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
// //           <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
// //             <div className="xl:col-span-2">
// //               <label
// //                 htmlFor="order-search"
// //                 className="mb-1 block text-sm font-medium text-slate-600"
// //               >
// //                 Search Order Number
// //               </label>

// //               <div className="relative">
// //                 <Search
// //                   size={18}
// //                   className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
// //                 />

// //                 <input
// //                   id="order-search"
// //                   type="search"
// //                   value={searchOrderNumber}
// //                   onChange={(event) =>
// //                     setSearchOrderNumber(event.target.value)
// //                   }
// //                   placeholder="Search by order number..."
// //                   className="w-full rounded-lg border border-slate-300 py-2.5 pl-10 pr-4 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
// //                 />
// //               </div>
// //             </div>

// //             <div>
// //               <label
// //                 htmlFor="order-from-date"
// //                 className="mb-1 block text-sm font-medium text-slate-600"
// //               >
// //                 From Date
// //               </label>

// //               <input
// //                 id="order-from-date"
// //                 type="date"
// //                 value={fromDate}
// //                 max={toDate || undefined}
// //                 onChange={(event) => setFromDate(event.target.value)}
// //                 className="w-full rounded-lg border border-slate-300 px-3 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
// //               />
// //             </div>

// //             <div>
// //               <label
// //                 htmlFor="order-to-date"
// //                 className="mb-1 block text-sm font-medium text-slate-600"
// //               >
// //                 To Date
// //               </label>

// //               <input
// //                 id="order-to-date"
// //                 type="date"
// //                 value={toDate}
// //                 min={fromDate || undefined}
// //                 onChange={(event) => setToDate(event.target.value)}
// //                 className="w-full rounded-lg border border-slate-300 px-3 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
// //               />
// //             </div>
// //           </div>

// //           {(searchOrderNumber || fromDate || toDate) && (
// //             <div className="mt-4 flex justify-end">
// //               <button
// //                 type="button"
// //                 onClick={clearFilters}
// //                 className="inline-flex items-center gap-2 rounded-lg bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200"
// //               >
// //                 <X size={16} />
// //                 Clear Filters
// //               </button>
// //             </div>
// //           )}
// //         </section>

// //         {/* ERROR */}
// //         {error && (
// //           <div className="mb-5 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
// //             <AlertCircle size={20} className="mt-0.5 shrink-0" />
// //             <div className="flex-1">
// //               <p className="font-medium">Unable to load order history</p>
// //               <p className="mt-1">{error}</p>
// //             </div>
// //             <button
// //               type="button"
// //               onClick={() => loadOrders()}
// //               className="font-semibold underline"
// //             >
// //               Retry
// //             </button>
// //           </div>
// //         )}

// //         {/* LOADING */}
// //         {loading ? (
// //           <div className="rounded-xl border border-slate-200 bg-white py-16 text-center">
// //             <RefreshCw
// //               size={30}
// //               className="mx-auto animate-spin text-blue-600"
// //             />
// //             <p className="mt-4 text-sm text-slate-500">
// //               Loading order history...
// //             </p>
// //           </div>
// //         ) : filteredOrders.length === 0 ? (
// //           <div className="rounded-xl border border-slate-200 bg-white px-4 py-16 text-center shadow-sm">
// //             <Receipt size={48} className="mx-auto text-slate-300" />

// //             <h3 className="mt-4 font-semibold text-slate-700">
// //               {error
// //                 ? "Orders could not be loaded"
// //                 : orders.length === 0
// //                   ? "No orders yet"
// //                   : "No orders found"}
// //             </h3>

// //             <p className="mt-2 text-sm text-slate-500">
// //               {error
// //                 ? "Check your connection and try again."
// //                 : orders.length === 0
// //                   ? "Orders will appear here when they become available."
// //                   : "Try changing your search or date filters."}
// //             </p>

// //             {error && (
// //               <button
// //                 type="button"
// //                 onClick={() => loadOrders()}
// //                 className="mt-4 rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
// //               >
// //                 Try Again
// //               </button>
// //             )}
// //           </div>
// //         ) : (
// //           <div className="space-y-4">
// //             {filteredOrders.map(renderOrder)}
// //           </div>
// //         )}
// //       </div>

// //       {/* ORDER DETAILS MODAL */}
// //       {selectedOrder && (
// //         <div
// //           className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-6"
// //           onMouseDown={(event) => {
// //             if (event.target === event.currentTarget) {
// //               setSelectedOrder(null);
// //             }
// //           }}
// //           onKeyDown={(event) => {
// //             if (event.key === "Escape") setSelectedOrder(null);
// //           }}
// //         >
// //           <div
// //             role="dialog"
// //             aria-modal="true"
// //             aria-labelledby="order-details-title"
// //             className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white shadow-xl"
// //           >
// //             <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white p-5">
// //               <div>
// //                 <h2
// //                   id="order-details-title"
// //                   className="text-xl font-bold text-slate-800"
// //                 >
// //                   Order Details
// //                 </h2>
// //                 <p className="mt-1 text-sm text-slate-500">
// //                   {selectedOrder.orderNumber || `Order #${selectedOrder.id}`}
// //                 </p>
// //               </div>

// //               <button
// //                 type="button"
// //                 onClick={() => setSelectedOrder(null)}
// //                 aria-label="Close order details"
// //                 className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
// //               >
// //                 <X size={22} />
// //               </button>
// //             </div>

// //             <div className="space-y-6 p-5">
// //               <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
// //                 <Detail
// //                   label="Order Number"
// //                   value={
// //                     selectedOrder.orderNumber || `#${selectedOrder.id}`
// //                   }
// //                 />
// //                 <Detail
// //                   label="Date & Time"
// //                   value={formatDate(selectedOrder.createdAt)}
// //                 />
// //                 <Detail
// //                   label="Order Type"
// //                   value={getOrderType(selectedOrder).label}
// //                 />
// //                 <Detail
// //                   label="Table"
// //                   value={
// //                     selectedOrder.orderType === "DINE_IN" &&
// //                     selectedOrder.table
// //                       ? selectedOrder.table.tableName
// //                       : "—"
// //                   }
// //                 />
// //                 <Detail
// //                   label="Status"
// //                   value={selectedOrder.status || "UNKNOWN"}
// //                 />
// //                 <Detail
// //                   label="Total Amount"
// //                   value={formatCurrency(getTotal(selectedOrder))}
// //                   emphasis
// //                 />
// //               </div>

// //               <section>
// //                 <h3 className="mb-3 font-semibold text-slate-800">
// //                   Billing Summary
// //                 </h3>

// //                 <div className="space-y-3 rounded-lg border bg-slate-50 p-4 text-sm">
// //                   <SummaryRow
// //                     label="Subtotal"
// //                     value={formatCurrency(getSubtotal(selectedOrder))}
// //                   />

// //                   {getDiscountAmount(selectedOrder) > 0 && (
// //                     <SummaryRow
// //                       label={`Discount${
// //                         getDiscountPercent(selectedOrder) > 0
// //                           ? ` (${getDiscountPercent(selectedOrder).toFixed(2)}%)`
// //                           : ""
// //                       }`}
// //                       value={`-${formatCurrency(
// //                         getDiscountAmount(selectedOrder)
// //                       )}`}
// //                       negative
// //                     />
// //                   )}

// //                   <SummaryRow
// //                     label="GST / Tax"
// //                     value={formatCurrency(getTaxAmount(selectedOrder))}
// //                   />

// //                   <div className="flex justify-between gap-4 border-t pt-3 text-base font-bold">
// //                     <span>Total</span>
// //                     <span className="text-green-600">
// //                       {formatCurrency(getTotal(selectedOrder))}
// //                     </span>
// //                   </div>
// //                 </div>
// //               </section>

// //               <section>
// //                 <h3 className="mb-3 font-semibold text-slate-800">
// //                   Order Note
// //                 </h3>
// //                 <div className="rounded-lg border bg-slate-50 p-4 text-sm text-slate-700">
// //                   {selectedOrder.specialInstructions?.trim() ||
// //                     "No notes added for this order."}
// //                 </div>
// //               </section>

// //               <section>
// //                 <div className="mb-3 flex items-center justify-between">
// //                   <h3 className="font-semibold text-slate-800">
// //                     Items
// //                   </h3>
// //                   <span className="text-sm text-slate-500">
// //                     {getTotalItems(selectedOrder)} total
// //                   </span>
// //                 </div>

// //                 {(selectedOrder.orderItems || []).length === 0 ? (
// //                   <p className="rounded-lg border p-4 text-sm text-slate-500">
// //                     No items found for this order.
// //                   </p>
// //                 ) : (
// //                   <div className="divide-y rounded-lg border">
// //                     {(selectedOrder.orderItems || []).map((item, index) => {
// //                       const price = Number(item.price || 0);
// //                       const quantity = Number(item.quantity || 0);
// //                       const tax = Number(item.taxAmount || 0);
// //                       const lineTotal =
// //                         Number(item.lineTotal || 0) || price * quantity;

// //                       return (
// //                         <div
// //                           key={item.id ?? `detail-item-${index}`}
// //                           className="flex justify-between gap-4 p-4"
// //                         >
// //                           <div className="min-w-0">
// //                             <p className="break-words font-medium text-slate-800">
// //                               {item.menuItem?.name || "Unknown Item"}
// //                             </p>
// //                             <p className="mt-1 text-xs text-slate-500">
// //                               {formatCurrency(price)} × {quantity}
// //                             </p>

// //                             {tax > 0 && (
// //                               <p className="mt-1 text-xs text-slate-500">
// //                                 GST {Number(item.taxRate || 0).toFixed(2)}% ·{" "}
// //                                 {formatCurrency(tax)}
// //                               </p>
// //                             )}

// //                             {item.specialInstructions?.trim() && (
// //                               <p className="mt-1 text-xs text-yellow-700">
// //                                 📝 {item.specialInstructions}
// //                               </p>
// //                             )}
// //                           </div>

// //                           <p className="whitespace-nowrap font-semibold text-slate-800">
// //                             {formatCurrency(lineTotal)}
// //                           </p>
// //                         </div>
// //                       );
// //                     })}
// //                   </div>
// //                 )}
// //               </section>
// //             </div>

// //             <div className="flex justify-end border-t p-4">
// //               <button
// //                 type="button"
// //                 onClick={() => setSelectedOrder(null)}
// //                 className="rounded-lg bg-slate-800 px-5 py-2.5 text-sm font-medium text-white hover:bg-slate-700"
// //               >
// //                 Close
// //               </button>
// //             </div>
// //           </div>
// //         </div>
// //       )}
// //     </div>
// //   );
// // }

// // function Detail({ label, value, emphasis = false }) {
// //   return (
// //     <div className="rounded-lg bg-slate-50 p-3">
// //       <p className="text-xs text-slate-500">{label}</p>
// //       <p
// //         className={`mt-1 break-words ${
// //           emphasis
// //             ? "text-lg font-bold text-green-600"
// //             : "font-semibold text-slate-800"
// //         }`}
// //       >
// //         {value}
// //       </p>
// //     </div>
// //   );
// // }

// // function SummaryRow({ label, value, negative = false }) {
// //   return (
// //     <div
// //       className={`flex justify-between gap-4 ${
// //         negative ? "text-red-600" : "text-slate-600"
// //       }`}
// //     >
// //       <span>{label}</span>
// //       <span className="whitespace-nowrap font-medium">{value}</span>
// //     </div>
// //   );
// // }
